import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { PlayCircle, ListChecks, Users2, ShieldCheck, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/common/BrandMark";
import { DefaultsFields } from "@/components/common/DefaultsFields";
import { FormAlert } from "@/components/common/Alert";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { GUARDRAIL, ROLE_LABELS } from "@/lib/constants";
import { firstName } from "@/lib/format";
import { cn } from "@/lib/utils";

const STEPS = ["Your details", "Your defaults", "How it works"];
const TOUR = [
  { icon: PlayCircle, title: "Start a job", body: "Pick a task on Home or find an agent. Each one opens its Agentic Brain run surface with your defaults already passed in." },
  { icon: ListChecks, title: "Run with approvals", body: "Workflows pause at human gates. Tick each gate as you pass it so you can pick up where you left off." },
  { icon: Users2, title: "Help each other", body: "Save prompts that worked to the Prompt Hub and give feedback on any agent, so the whole team gets better results." },
];

export default function Welcome() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    name: user.name, service_line: user.service_line, audience_stream: user.audience_stream, region: user.region,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user.onboarded) return <Navigate to="/" replace />;

  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const canNext = step === 0 ? values.name.trim() : step === 1 ? values.service_line && values.audience_stream && values.region : true;

  const finish = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch("/me", { ...values, onboarded: true });
      setUser(data);
      navigate("/", { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-backdrop min-h-screen px-4 py-12" data-testid="welcome-page">
      <div className="mx-auto max-w-3xl">
        <div className="mb-10 flex items-center gap-3"><BrandMark /><span className="text-sm font-medium text-muted-foreground">Getting started</span></div>
        <h1 className="text-xl font-semibold md:text-2xl">Welcome, {firstName(values.name) || "there"}</h1>
        <ol className="mt-6 flex flex-wrap gap-2" aria-label="Progress">
          {STEPS.map((s, i) => (
            <li key={s} className={cn("flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium", i === step ? "border-foreground text-foreground" : "text-muted-foreground")} aria-current={i === step ? "step" : undefined} data-testid={`welcome-step-${i + 1}`}>
              <span className={cn("grid h-5 w-5 place-items-center rounded-full text-[11px]", i < step ? "bg-success text-white" : "bg-muted")}>{i < step ? <Check className="!h-3 !w-3" aria-hidden="true" /> : i + 1}</span>
              {s}
            </li>
          ))}
        </ol>

        <div className="card-elevated mt-8 p-8">
          <FormAlert testid="welcome-error">{error}</FormAlert>
          {step === 0 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="welcome-name">Is this how your name should appear?</Label>
                <Input id="welcome-name" value={values.name} maxLength={120} onChange={(e) => onChange("name", e.target.value)} data-testid="welcome-name-input" />
              </div>
              <div>
                <p className="text-sm font-medium">Your role</p>
                <p className="mt-1 text-sm text-muted-foreground" data-testid="welcome-role">{ROLE_LABELS[user.role]}. An Owner sets roles; ask one if this looks wrong.</p>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">These defaults go into every run. You can change them on your profile or for a single run.</p>
              <DefaultsFields values={values} onChange={onChange} />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                {TOUR.map(({ icon: Icon, title, body }, i) => (
                  <div key={title} className="rounded-xl border p-5" data-testid={`welcome-tour-${i + 1}`}>
                    <Icon className="text-foreground" aria-hidden="true" />
                    <h2 className="mt-3 text-base font-semibold">{i + 1}. {title}</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{body}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-3 rounded-xl bg-muted px-4 py-3" data-testid="welcome-guardrail">
                <ShieldCheck className="mt-0.5 shrink-0" aria-hidden="true" />
                <p className="text-sm font-medium">{GUARDRAIL}</p>
              </div>
            </div>
          )}
          <div className="mt-8 flex justify-between">
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="welcome-back-button">Back</Button>
            {step < 2 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="welcome-next-button">Continue</Button>
            ) : (
              <Button onClick={finish} disabled={busy} data-testid="welcome-finish-button">{busy ? "Saving" : "Go to the hub"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
